import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import { Application, Company, CompanyApplication, Version } from '../../../../../interfaces/users.interface';
import { HardeningDocument } from '../../../../../interfaces/hardeningDocuments';
import { CompanyTabListParams } from '../../../../../interfaces/companyDetail.interface';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { fetchDocument } from '../../../../../app/store/serviceCategories';
import { getCompanyApps } from '../../../../../services/companyDetail';
import { useCompanyResource } from '../../../../Vendors/useCompanyResource';
import { TabPagination } from '../../../../Vendors/companyTabUi';
import VersionSidebarV2 from './VersionSidebarV2';
import InhouseAppCard from './InhouseAppCard';

const APPS_PER_PAGE = 5;

// Server-paginated in-house apps (application owned by this company), each row
// carrying its embedded certifications — no full-list fetch.
const inhouseFetcher = (id: number | string, params?: CompanyTabListParams) =>
  getCompanyApps(id, { ...params, kind: 'inhouse' });

type InhouseApplicationsCardsProps = {
  company: Company;
  userCanAdminCompany: boolean;
  companyCallback: any;
  reloadToken?: number;
};

const InhouseApplicationsCards = (props: InhouseApplicationsCardsProps) => {
  const { company, userCanAdminCompany, companyCallback, reloadToken } = props;
  const dispatch = useAppDispatch();
  const { documents } = useAppSelector((state) => state.serviceCategories);

  const [page, setPage] = useState(1);
  const { items: applications, count, loading, refetch } = useCompanyResource<CompanyApplication>(
    inhouseFetcher,
    company.id,
    page,
    APPS_PER_PAGE,
  );

  // Re-fetch this page when the tab-level add modal reports a change.
  useEffect(() => {
    if (reloadToken) refetch();
  }, [reloadToken]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedCompanyApp, setSelectedCompanyApp] = useState<CompanyApplication | undefined>(undefined);
  const [selectedApp, setSelectedApp] = useState<Application | undefined>(undefined);
  const [selectedVersion, setSelectedVersion] = useState<Version | undefined>(undefined);

  // Keep the parent in sync (header/labels) and refetch this page after a change.
  const handleChange = useCallback((updatedCompany: Company) => {
    companyCallback(updatedCompany);
    refetch();
  }, [companyCallback, refetch]);

  const handleVersionRowClick = useCallback((companyApp: CompanyApplication, app: Application, version: Version) => {
    setSelectedCompanyApp(companyApp);
    setSelectedApp(app);
    setSelectedVersion(version);
    setIsSidebarOpen(true);
    // The sidebar exposes edit, whose form needs services/sites — prefetch now.
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
    setSelectedApp(undefined);
    setSelectedVersion(undefined);
  }, []);

  const handleSidebarEditSave = useCallback((comp: Company, companyApp: CompanyApplication, type: 'edit' | 'delete') => {
    handleChange(comp);
    if (type !== 'delete') {
      setSelectedCompanyApp(companyApp);
      setSelectedApp(companyApp.application);
    } else {
      setIsSidebarOpen(false);
    }
  }, [handleChange]);

  // Prefetch config-guideline (hardening) documents for the apps on this page.
  useEffect(() => {
    applications
      .filter((ca) => ca.application.hardeningDocuments && ca.application.hardeningDocuments.length > 0)
      .forEach((ca) =>
        (ca.application.hardeningDocuments as HardeningDocument[])?.forEach((hd) => {
          if (!documents.find((doc) => doc.id === hd.document)) dispatch(fetchDocument(hd.document));
        }),
      );
  }, [applications]);

  return (
    <Box>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.05em' }}>
        In-House Developed Applications ({count})
      </Typography>

      {loading ? (
        <>
          <Skeleton variant='rectangular' height={120} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant='rectangular' height={120} sx={{ borderRadius: 2 }} />
        </>
      ) : applications.length > 0 ? (
        applications.map((companyApplication: CompanyApplication) => (
          <InhouseAppCard
            key={companyApplication.id}
            company={company}
            companyApplication={companyApplication}
            userCanAdminCompany={userCanAdminCompany}
            companySuccessCallback={handleChange}
            onVersionRowClick={handleVersionRowClick}
          />
        ))
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <AppsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant='body2' color='text.secondary'>
            No in-house applications
          </Typography>
        </Paper>
      )}

      <TabPagination count={count} page={page} pageSize={APPS_PER_PAGE} onChange={setPage} />

      <VersionSidebarV2
        isOpen={isSidebarOpen}
        company={company}
        companyApp={selectedCompanyApp}
        application={selectedApp}
        version={selectedVersion}
        onClose={handleSidebarClose}
        userCanAdminCompany={userCanAdminCompany}
        companySuccessCallback={handleSidebarEditSave}
      />
    </Box>
  );
};

export default InhouseApplicationsCards;
